import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, AlertTriangle } from 'lucide-react';
import { formatDate, calculateRemainingTime } from './utils';
import type { Membership } from './types';

interface RemainingTimeCardProps {
  membership: Membership;
}

export const RemainingTimeCard: React.FC<RemainingTimeCardProps> = ({ membership }) => {
  const { value, unit, progress } = calculateRemainingTime(membership);
  
  // Dynamic colors based on urgency
  const getProgressColor = () => {
    if (progress > 50) return 'from-emerald-500 to-green-400';
    if (progress > 25) return 'from-amber-500 to-yellow-400';
    return 'from-red-500 to-orange-400';
  };
  
  const getGlowColor = () => {
    if (progress > 50) return 'shadow-emerald-500/30';
    if (progress > 25) return 'shadow-amber-500/30';
    return 'shadow-red-500/30';
  };
  
  const getTextColor = () => {
    if (progress > 50) return 'text-emerald-400';
    if (progress > 25) return 'text-amber-400';
    return 'text-red-400';
  };

  const isLowTime = progress <= 25;

  // Calculate stroke dasharray for circular progress
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10 relative overflow-hidden"
    >
      {/* Low time pulse effect */}
      {isLowTime && (
        <motion.div
          animate={{ opacity: [0.1, 0.3, 0.1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10"
        />
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <motion.div
            animate={isLowTime ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5, repeat: isLowTime ? Infinity : 0, repeatDelay: 2 }}
          >
            {isLowTime ? (
              <AlertTriangle className="w-5 h-5 text-red-400" />
            ) : (
              <Calendar className="w-5 h-5 text-blue-500" />
            )}
          </motion.div>
        <span className="text-white font-medium">
          {membership.billing_period === 'anual' 
            ? 'Plan Anual - Tiempo Restante' 
            : 'Tiempo Restante'
          }
        </span>
        </div>
      </div>

      <div className="flex items-center gap-6">
        {/* Circular progress */}
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg className="w-full h-full transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="56"
              cy="56"
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <motion.circle
              cx="56"
              cy="56"
              r={radius}
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="8"
              strokeLinecap="round"
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              style={{
                strokeDasharray: circumference,
              }}
            />
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={progress > 50 ? '#10b981' : progress > 25 ? '#f59e0b' : '#ef4444'} />
                <stop offset="100%" stopColor={progress > 50 ? '#34d399' : progress > 25 ? '#fbbf24' : '#f97316'} />
              </linearGradient>
            </defs>
          </svg>
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.span 
              key={value}
              initial={{ scale: 1.2, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`text-3xl font-bold ${getTextColor()}`}
            >
              {value}
            </motion.span>
            <span className="text-white/60 text-sm">{unit}</span>
          </div>
        </div>

        {/* Date info */}
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-white/40" />
            <div>
              <p className="text-white/50">Inicio</p>
              <p className="text-white font-medium">{formatDate(membership.start_date)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-white/40" />
            <div>
              <p className="text-white/50">Vencimiento</p>
              <p className={`font-medium ${isLowTime ? 'text-red-400' : 'text-white'}`}>
                {formatDate(membership.end_date)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Linear progress bar */}
      <div className="mt-4">
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className={`h-full bg-gradient-to-r ${getProgressColor()} rounded-full shadow-lg ${getGlowColor()}`}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default RemainingTimeCard;
